"use client";

import { useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { getPublicRuntimeConfig } from "@/lib/config/public-runtime-config";

export interface UserRole {
  id: number;
  email: string;
  role: "OWNER" | "ROOT" | "ADMIN" | "USER";
  firstName?: string;
  lastName?: string;
}

const CACHE_KEY = "primus:userRole";
const CACHE_TTL_MS = 5 * 60 * 1000;
const RATE_LIMIT_BACKOFF_MS = 30 * 1000;

let cachedRole: UserRole | null = null;
let cachedAt = 0;
let inFlight: Promise<UserRole | null> | null = null;
let lastRateLimitAt = 0;

const readCachedRole = (): { role: UserRole; at: number } | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { role?: UserRole; at?: number };
    if (!parsed.role || !parsed.at) return null;
    return { role: parsed.role, at: parsed.at };
  } catch {
    return null;
  }
};

const writeCachedRole = (role: UserRole): void => {
  cachedRole = role;
  cachedAt = Date.now();
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ role, at: cachedAt })
    );
  } catch {
    // ignore storage errors
  }
};

const clearCachedRole = (): void => {
  cachedRole = null;
  cachedAt = 0;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore storage errors
  }
};

/**
 * Hook to fetch and check user role from backend
 */
export function useUserRole() {
  const { getToken, isSignedIn } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { apiUrl } = getPublicRuntimeConfig();

  useEffect(() => {
    const isUserRole = (value: unknown): value is UserRole => {
      if (!value || typeof value !== "object") return false;
      const record = value as Record<string, unknown>;
      const role = record.role;
      return (
        typeof record.id === "number" &&
        typeof record.email === "string" &&
        (role === "OWNER" ||
          role === "ROOT" ||
          role === "ADMIN" ||
          role === "USER")
      );
    };

    async function fetchUserRole() {
      if (!isSignedIn) {
        clearCachedRole();
        setUserRole(null);
        setLoading(false);
        return;
      }

      const now = Date.now();

      if (cachedRole && now - cachedAt < CACHE_TTL_MS) {
        setUserRole(cachedRole);
        setLoading(false);
        return;
      }

      const stored = readCachedRole();
      if (stored && now - stored.at < CACHE_TTL_MS) {
        cachedRole = stored.role;
        cachedAt = stored.at;
        setUserRole(stored.role);
        setLoading(false);
        return;
      }

      if (now - lastRateLimitAt < RATE_LIMIT_BACKOFF_MS) {
        setUserRole(cachedRole ?? stored?.role ?? null);
        setLoading(false);
        return;
      }

      if (!inFlight) {
        inFlight = (async () => {
          try {
            const token = await getToken();
            if (!token) {
              return cachedRole;
            }
            const response = await fetch(`${apiUrl}/user/profile`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.status === 401) {
              return null;
            }

            if (response.status === 429) {
              lastRateLimitAt = Date.now();
              return cachedRole ?? null;
            }

            if (!response.ok) {
              throw new Error("Failed to fetch user role");
            }

            const data = (await response.json().catch(() => null)) as unknown;
            if (!isUserRole(data)) {
              throw new Error("Invalid user role response");
            }

            writeCachedRole(data);
            return data;
          } finally {
            inFlight = null;
          }
        })();
      }

      try {
        const role = await inFlight;
        if (role) {
          setUserRole(role);
        } else if (cachedRole) {
          setUserRole(cachedRole);
        } else {
          setUserRole(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        setUserRole(cachedRole ?? stored?.role ?? null);
      } finally {
        setLoading(false);
      }
    }

    void fetchUserRole();
  }, [apiUrl, getToken, isSignedIn]);

  const isAdmin =
    userRole?.role === "ADMIN" ||
    userRole?.role === "ROOT" ||
    userRole?.role === "OWNER";
  const isOwner = userRole?.role === "OWNER";
  const isRoot = userRole?.role === "ROOT" || userRole?.role === "OWNER";

  return {
    userRole,
    loading,
    error,
    isAdmin,
    isOwner,
    isRoot,
    isSignedIn,
  };
}
